/**
 * Resolve which environment the Flow Designer should auto-warm as the sandbox
 * run target.
 *
 * Precedence mirrors the run-time target resolver
 * (apps/api/src/workflows/helpers/suite-environment.ts: resolveFlowTargetEnv):
 *   1. the flow's own default env, when it is an eligible sandbox;
 *   2. the property default env, when it is a sandbox;
 *   3. the sole eligible env, when it is a sandbox.
 *
 * Returns null when no sandbox is the resolved warm target. Kept as a pure
 * function so the precedence is unit-testable independent of the designer.
 */
export interface SandboxTargetEnv {
  id: string;
  name: string;
  type: 'permanent' | 'ephemeral' | 'local' | 'sandbox';
  isDefault: boolean;
}

export function resolveSandboxWarmTarget(
  environments: readonly SandboxTargetEnv[],
  eligibleEnvironmentIds: readonly string[],
  workflowDefaultEnvironmentId: string | null
): SandboxTargetEnv | null {
  // 1. The flow's own default env, when it is an eligible sandbox.
  if (workflowDefaultEnvironmentId) {
    const wfDefault = environments.find((e) => e.id === workflowDefaultEnvironmentId);
    if (wfDefault?.type === 'sandbox' && eligibleEnvironmentIds.includes(wfDefault.id)) {
      return wfDefault;
    }
  }
  // 2. The property default env, when it is a sandbox.
  const defaultEnv = environments.find((e) => e.isDefault);
  if (defaultEnv?.type === 'sandbox') return defaultEnv;
  // 3. The sole eligible env, when it is a sandbox.
  const eligible = environments.filter((e) => eligibleEnvironmentIds.includes(e.id));
  if (eligible.length === 1 && eligible[0]!.type === 'sandbox') return eligible[0]!;
  return null;
}
